/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState, memo } from 'react'
import { FormHelperText } from '@maas-components/core'
import { OutlinedEditor, QuillEditor } from '@maas-components/editor'
import PropTypes from 'prop-types'
import { useDebouncedCallback } from 'use-debounce'
import i18n from 'core/common/i18n'
import { Box, FormControl, Grid } from 'core/components/Core'
import EditorTips from './EditorTips'
import { clearHtml, countCharactersWSpace, allowedHtml } from './utils/sanitize'

function EditorField({
  value,
  setFieldValue,
  setFieldTouched,
  error,
  helperText,
  hasEditorTips,
  onNext,
  onReload,
  custom,
  hasHtmlFormat,
  onChange,
  initialValues,
}) {
  const [count, setCount] = useState(countCharactersWSpace(clearHtml(value)))
  const [field, setField] = useState('')
  const toolbar = [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ]

  const formats = ['bold', 'italic', 'underline', 'list']

  /**
   * Método que enviar o valor para o contexto do formik com debounce.
   */
  const debouncedHandleOnChange = useDebouncedCallback(editor => {
    if (!hasHtmlFormat) {
      const text = editor.getText()
      setFieldValue('description', text)

      if (text.trim() !== initialValues.description.trim() && onChange) {
        onChange()
      }

      return
    }

    const html = editor.getHTML()
    const text = allowedHtml(html)

    setFieldValue('description', text)

    if (text.trim() !== initialValues.description.trim() && onChange) {
      onChange()
    }
  }, 600)

  /**
   * Método que recebe o referencial do editor para pegar os caracteres que estão no input.
   */
  const handleEditor = editor => {
    const editorText = editor.getText().replace(/\n(?!.*\n)/, '')
    const length = countCharactersWSpace(editorText)
    setCount(length)
    setField(editor)
    debouncedHandleOnChange.callback(editor)
  }

  /**
   * Método memorizado para setar o touched no contexto do formik.
   */
  const handleOnBlur = useCallback(() => setFieldTouched('description', true), [])

  /**
   * Método memorizado para setar o referencial para uma função (obs: para evitar crash nos helpers).
   */
  const handleOnChange = (_content, _delta, _source, editor) => {
    handleEditor(editor)
  }

  const renderEditorTips = () => {
    if (!hasEditorTips) return null

    return <EditorTips />
  }

  // useEffect(() => {
  //   if (typeof field === 'object') {
  //     handleEditor(field)
  //   }
  // }, [onNext, onReload])

  useEffect(() => {
    if (typeof field !== 'object') return setCount(0)
  }, [field, onNext, onReload])

  return (
    <Grid container item md={12}>
      <Grid item xs={12}>
        <Box mt={hasEditorTips ? 4 : 0}>
          <Grid container>
            {renderEditorTips()}
            <Grid item xs={12} md={hasEditorTips ? 8 : 12}>
              <Box mt={hasEditorTips ? 6 : 0} ml={hasEditorTips ? 2 : 0}>
                <FormControl fullWidth variant="outlined">
                  <OutlinedEditor error={error} translate="no" className="word-break">
                    <QuillEditor
                      name="description"
                      onBlur={handleOnBlur}
                      onChange={handleOnChange}
                      defaultValue={value}
                      placeholder={i18n.t('product.scopes.description.placeholderEditor')}
                      modules={
                        custom
                          ? {
                              toolbar: false,
                              keyboard: { bindings: {} },
                              clipboard: { matchVisual: false },
                            }
                          : { toolbar }
                      }
                      formats={custom ? [] : formats}
                      preserveWhitespace
                      rows={4}
                    />
                  </OutlinedEditor>
                  <Grid container>
                    <Grid item md={6} xs={12}>
                      <FormHelperText error={error}>{helperText}</FormHelperText>
                    </Grid>
                    <Grid container item md={6} xs={12} justifyContent="flex-end">
                      <FormHelperText>
                        {`${count} ${i18n.t(
                          custom?.descriptionTextTimit ||
                            'product.scopes.description.descriptionLimit',
                          { max: custom?.maxLimit ?? 5000 }
                        )}`}
                      </FormHelperText>
                    </Grid>
                  </Grid>
                </FormControl>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Grid>
    </Grid>
  )
}

EditorField.propTypes = {
  error: PropTypes.any,
  helperText: PropTypes.any,
  setFieldValue: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
  value: PropTypes.any.isRequired,
  hasEditorTips: PropTypes.bool,
}

EditorField.defaultProps = {
  error: null,
  helperText: null,
  hasEditorTips: false,
}

/**
 * Memorização da propriedade description, afim de evitar re-render do componente.
 */
export default memo(EditorField)
